'use client'

import {
    ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    Row,
    RowSelectionState,
} from '@tanstack/react-table'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'

import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    type Device,
    selectColumn,
    baseColumns,
    cloudActiveColumn,
} from './columns'
import { authClient } from '@/lib/auth-client'
import { BulkUpdateModal } from './bulk-update-modal'
import { Download } from 'lucide-react'

interface DataTableProps {
    data: Device[]
}

export function DataTable({ data }: DataTableProps) {
    const [sorting, setSorting] = useState<SortingState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [businessFilter, setBusinessFilter] = useState('all')
    const [modelFilter, setModelFilter] = useState('all')
    const [svFilter, setSvFilter] = useState('all')
    const [cloudFilter, setCloudFilter] = useState('all')
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
    const [bulkAction, setBulkAction] = useState<'softwareVersion' | 'business' | 'model' | 'cloudActive' | 'cloudActiveOverride' | null>(null)

    const navigate = useNavigate()
    const { data: session } = authClient.useSession()
    const role = (session?.user as any)?.role
    const isSystemAdmin = role === 'systemAdmin'

    // Build columns based on role
    const columns = useMemo(() => {
        const cols: ColumnDef<Device>[] = []
        if (isSystemAdmin) cols.push(selectColumn)
        cols.push(...baseColumns)
        if (isSystemAdmin) cols.push(cloudActiveColumn)
        return cols
    }, [isSystemAdmin])

    // Extract unique values for filter dropdowns
    const businesses = useMemo(() => {
        const set = new Set(data.map((d) => d.business?.name).filter(Boolean))
        return Array.from(set).sort()
    }, [data])

    const models = useMemo(() => {
        const set = new Set(data.map((d) => d.model?.name).filter(Boolean))
        return Array.from(set).sort()
    }, [data])

    const softwareVersions = useMemo(() => {
        const set = new Set(
            data.map((d) => d.softwareVersion?.name).filter(Boolean)
        )
        return Array.from(set).sort()
    }, [data])

    // Apply filters
    const filteredData = useMemo(() => {
        let result = data

        if (globalFilter) {
            const q = globalFilter.toLowerCase()
            result = result.filter(
                (d) =>
                    d.name?.toLowerCase().includes(q) ||
                    d.serial?.toLowerCase().includes(q) ||
                    d.business?.name?.toLowerCase().includes(q)
            )
        }

        if (statusFilter !== 'all') {
            result = result.filter((d) => d.onlineStatus === statusFilter)
        }

        if (businessFilter !== 'all') {
            result = result.filter((d) => d.business?.name === businessFilter)
        }

        if (modelFilter !== 'all') {
            result = result.filter((d) => d.model?.name === modelFilter)
        }

        if (isSystemAdmin && svFilter !== 'all') {
            result = result.filter((d) => d.softwareVersion?.name === svFilter)
        }

        if (isSystemAdmin && cloudFilter !== 'all') {
            result = result.filter((d) => {
                if (cloudFilter === 'active') return d.cloudActive || d.cloudActiveOverride
                if (cloudFilter === 'inactive') return !d.cloudActive && !d.cloudActiveOverride
                if (cloudFilter === 'overridden') return d.cloudActiveOverride
                return true
            })
        }

        return result
    }, [
        data,
        globalFilter,
        statusFilter,
        businessFilter,
        modelFilter,
        svFilter,
        cloudFilter,
        isSystemAdmin,
    ])

    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onRowSelectionChange: setRowSelection,
        state: { sorting, rowSelection },
        getRowId: (row) => row.id,
    })

    const handleRowClick = (row: Row<Device>) => {
        navigate(`/devices/${row.original.id}`)
    }

    // Selection logic for bulk update
    const selectedRows = table.getFilteredSelectedRowModel().rows
    const selectedDevices = selectedRows.map((r) => r.original)
    const selectedModels = new Set(selectedDevices.map((d) => d.model?.name))
    const allSameModel = selectedModels.size === 1

    return (
        <>
            {/* Filters */}
            <div className="flex flex-col gap-3 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                    <Input
                        placeholder="Search devices..."
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        className="max-w-xs h-9"
                    />
                    <Select
                        value={statusFilter}
                        onValueChange={setStatusFilter}
                    >
                        <SelectTrigger className="w-[130px] h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="offline">Offline</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={businessFilter}
                        onValueChange={setBusinessFilter}
                    >
                        <SelectTrigger className="w-[160px] h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Businesses</SelectItem>
                            {businesses.map((b) => (
                                <SelectItem key={b} value={b!}>
                                    {b}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={modelFilter} onValueChange={setModelFilter}>
                        <SelectTrigger className="w-[160px] h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Models</SelectItem>
                            {models.map((m) => (
                                <SelectItem key={m} value={m!}>
                                    {m}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {isSystemAdmin && (
                        <>
                            <Select
                                value={svFilter}
                                onValueChange={setSvFilter}
                            >
                                <SelectTrigger className="w-[160px] h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">
                                        All Versions
                                    </SelectItem>
                                    {softwareVersions.map((v) => (
                                        <SelectItem key={v} value={v!}>
                                            {v}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={cloudFilter}
                                onValueChange={setCloudFilter}
                            >
                                <SelectTrigger className="w-[140px] h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">
                                        Cloud: All
                                    </SelectItem>
                                    <SelectItem value="active">
                                        Cloud Active
                                    </SelectItem>
                                    <SelectItem value="inactive">
                                        Cloud Inactive
                                    </SelectItem>
                                    <SelectItem value="overridden">
                                        Cloud Overridden
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </>
                    )}
                </div>

                {/* Bulk action bar */}
                {isSystemAdmin && selectedDevices.length > 0 && (
                    <div className="flex items-center gap-3 rounded-lg border bg-accent/50 px-4 py-2 flex-wrap">
                        <span className="text-sm text-muted-foreground">
                            {selectedDevices.length} device
                            {selectedDevices.length !== 1 ? 's' : ''} selected
                        </span>
                        <Button size="sm" variant="outline" onClick={() => setBulkAction('business')}>
                            Set Business
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setBulkAction('model')}>
                            Set Model
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setBulkAction('cloudActive')}>
                            Set Cloud Active
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setBulkAction('cloudActiveOverride')}>
                            Set Cloud Override
                        </Button>
                        {allSameModel ? (
                            <Button size="sm" variant="outline" onClick={() => setBulkAction('softwareVersion')}>
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                Set Software Version
                            </Button>
                        ) : (
                            <span className="text-xs text-muted-foreground italic">
                                Same model required for software version
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="rounded-md border w-full bg-white">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef
                                                      .header,
                                                  header.getContext()
                                              )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length > 0 ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={
                                        row.getIsSelected() && 'selected'
                                    }
                                    onClick={() => handleRowClick(row)}
                                    className="cursor-pointer"
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="text-center py-8"
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Bulk update modal */}
            {bulkAction && (
                <BulkUpdateModal
                    open={!!bulkAction}
                    onOpenChange={(open) => { if (!open) setBulkAction(null) }}
                    devices={selectedDevices}
                    action={bulkAction}
                />
            )}
        </>
    )
}
